/**
 * Build the Plotly traces + layout for the cluster map (design doc 015 §6).
 *
 * Kept as a pure module — separate from the `ClusterScatter` island that loads
 * Plotly lazily — so the map-assembly logic (one translucent convex-hull fill +
 * one marker trace per mode, a single grey trace for the unassigned long tail,
 * selection emphasis) is unit-tested without pulling the ~3 MB Plotly bundle
 * into jest.
 *
 * Trace/layout objects are typed locally (a thin subset of Plotly's surface) so
 * this module needs no Plotly types either.
 */

import type { FailureMode, FailurePoint } from "../types";
import { paddedHull } from "./hull";
import { hexToRgba, UNASSIGNED_COLOR } from "./palette";

export interface PlotlyTrace {
  x: number[];
  y: number[];
  type: "scatter" | "scattergl";
  mode: "markers" | "lines";
  name?: string;
  marker?: {
    size?: number;
    color?: string;
    opacity?: number;
    line?: { width: number; color: string };
  };
  line?: { color: string; width: number };
  fill?: "toself";
  fillcolor?: string;
  text?: string[];
  customdata?: string[];
  hovertemplate?: string;
  hoverinfo?: "skip";
  showlegend: boolean;
  legendgroup?: string;
}

export interface PlotlyLayout {
  hovermode: "closest";
  hoverlabel: { align: "left" };
  showlegend: false;
  dragmode: "pan";
  margin: { l: number; r: number; t: number; b: number };
  xaxis: Record<string, unknown>;
  yaxis: Record<string, unknown>;
  paper_bgcolor: string;
  plot_bgcolor: string;
  font: { family: string };
}

const MARKER_SIZE = 7;
const MARKER_SIZE_SELECTED = 10;
const NOISE_SIZE = 5;
// Opacity of points/hulls for modes that are NOT the selected one, when a
// selection is active — selecting a mode in the legend dims the rest.
const DIMMED = 0.15;
// Hull padding as a fraction of the data span — buffers each mode's points so
// the boundary encloses the markers and 1-2-point/collinear modes still get a
// visible hull (#1274).
const HULL_PAD_FRACTION = 0.015;

/** Padding radius in data units: a fraction of the larger axis span. */
function hullPadRadius(points: FailurePoint[]): number {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  const span = Math.max(maxX - minX, maxY - minY);
  // Zero span ⇒ every in-window point is coincident, so there is no scale to
  // derive a radius from; any positive constant works because Plotly autoscales
  // the axes to the resulting circle.
  return span > 0 ? span * HULL_PAD_FRACTION : 1;
}

/** Soft-wrap an explanation with <br> for the Plotly hover box (no truncation). */
export function wrapHover(text: string, width = 72): string {
  const escaped = (text || "")
    .trim()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  if (escaped.length <= width) return escaped;
  const out: string[] = [];
  let rest = escaped;
  while (rest.length > width) {
    let cut = rest.lastIndexOf(" ", width);
    if (cut <= 0) cut = width;
    out.push(rest.slice(0, cut));
    rest = rest.slice(cut).trimStart();
  }
  if (rest) out.push(rest);
  return out.join("<br>");
}

/** Group in-window points by failure mode id; `null` id = the unassigned pool. */
function groupByMode(
  points: FailurePoint[],
): Map<string | null, FailurePoint[]> {
  const groups = new Map<string | null, FailurePoint[]>();
  for (const p of points) {
    const key = p.failure_mode_id;
    const bucket = groups.get(key);
    if (bucket) bucket.push(p);
    else groups.set(key, [p]);
  }
  return groups;
}

export interface ScatterFigure {
  data: PlotlyTrace[];
  layout: PlotlyLayout;
}

/**
 * Assemble the figure. `modes` fixes the draw order (the API's stable mint
 * order, #1260) and which ids get a hull; `points` supply the positions —
 * modes without in-window points are skipped. Only modes present in `modes` are
 * drawn (hull + markers), so the caller hides the long tail by passing a subset
 * (#1297). `colorForMode` maps a mode id to its palette colour (session-stable,
 * assigned so co-visible modes stay distinct — see `useModeColors`).
 * `selectedModeId` (if any) is drawn at full strength while the rest dim.
 * `showUnassigned` toggles the grey long-tail trace (off cuts clutter).
 */
export function buildScatterFigure(
  points: FailurePoint[],
  modes: FailureMode[],
  selectedModeId: string | null,
  colorForMode: (id: string) => string,
  showUnassigned = true,
  // Marker outline — the thin ring that separates overlapping points. It should
  // match the plot surface (card) so it reads as a cut-out, not a coloured ring;
  // the caller passes the theme's surface colour. Defaults to white for the
  // light theme (and so the pure unit tests need no theme context).
  markerLineColor = "white",
): ScatterFigure {
  const groups = groupByMode(points);
  const data: PlotlyTrace[] = [];

  const emphasis = (modeId: string): number =>
    selectedModeId && selectedModeId !== modeId ? DIMMED : 1;

  // Hull fills first (drawn under the markers). One per mode with in-window
  // points — padding guarantees a polygon even for 1-2-point modes (#1274).
  const pad = hullPadRadius(points);
  for (const mode of modes) {
    const pts = groups.get(mode.failure_mode_id);
    if (!pts || pts.length === 0) continue;
    const hull = paddedHull(
      pts.map((p) => ({ x: p.x, y: p.y })),
      pad,
    );
    if (hull.length < 3) continue;
    const color = colorForMode(mode.failure_mode_id);
    const selected = selectedModeId === mode.failure_mode_id;
    const alpha = selected ? 0.22 : selectedModeId ? 0.05 : 0.12;
    data.push({
      x: [...hull.map((h) => h.x), hull[0].x],
      y: [...hull.map((h) => h.y), hull[0].y],
      type: "scatter",
      mode: "lines",
      line: { color, width: 1.5 },
      fill: "toself",
      fillcolor: hexToRgba(color, alpha),
      hoverinfo: "skip",
      showlegend: false,
      legendgroup: mode.failure_mode_id,
    });
  }

  // Marker trace per mode (WebGL — comfortably handles the 5k point cap).
  for (const mode of modes) {
    const pts = groups.get(mode.failure_mode_id);
    if (!pts || pts.length === 0) continue;
    const color = colorForMode(mode.failure_mode_id);
    const selected = selectedModeId === mode.failure_mode_id;
    data.push({
      x: pts.map((p) => p.x),
      y: pts.map((p) => p.y),
      type: "scattergl",
      mode: "markers",
      name: mode.label,
      marker: {
        size: selected ? MARKER_SIZE_SELECTED : MARKER_SIZE,
        color,
        opacity: emphasis(mode.failure_mode_id),
        line: { width: 0.5, color: markerLineColor },
      },
      text: pts.map((p) => wrapHover(p.explanation)),
      customdata: pts.map(() => mode.failure_mode_id),
      hovertemplate: "%{text}<extra></extra>",
      showlegend: false,
      legendgroup: mode.failure_mode_id,
    });
  }

  // The unassigned long tail — small grey dots, no hull, no click target.
  const unassigned = groups.get(null);
  if (showUnassigned && unassigned && unassigned.length > 0) {
    data.push({
      x: unassigned.map((p) => p.x),
      y: unassigned.map((p) => p.y),
      type: "scattergl",
      mode: "markers",
      name: "Unclustered",
      marker: {
        size: NOISE_SIZE,
        color: UNASSIGNED_COLOR,
        opacity: selectedModeId ? DIMMED : 0.55,
      },
      text: unassigned.map((p) => wrapHover(p.explanation)),
      hovertemplate: "%{text}<extra></extra>",
      showlegend: false,
      legendgroup: "unassigned",
    });
  }

  const layout: PlotlyLayout = {
    hovermode: "closest",
    // Force left-aligned hover text. Plotly's default ("auto") right-aligns the
    // box when it sits to the left of the point (points on the right edge of the
    // map), so the wrapped explanation flips alignment depending on position
    // (#1287). Pin it left so every hover reads the same way.
    hoverlabel: { align: "left" },
    showlegend: false,
    dragmode: "pan",
    margin: { l: 8, r: 8, t: 8, b: 8 },
    xaxis: { visible: false, zeroline: false, showgrid: false },
    yaxis: {
      visible: false,
      zeroline: false,
      showgrid: false,
      scaleanchor: "x",
      scaleratio: 1,
    },
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: "rgba(0,0,0,0)",
    font: { family: "DMSans, ui-sans-serif, system-ui, sans-serif" },
  };

  return { data, layout };
}
